import dayjs from "dayjs";
import { Patient, Person, Practitioner, Reference } from "fhir/r5";
import { getOfficialIdentifierValue, getReferenceValue } from "./fhirReferences";

export type IndividualResource = Person | Patient | Practitioner;
export type IndividualResourceType = "Person" | "Patient" | "Practitioner";
export type IndividualSection = "users" | "patients" | "practitioners";

export type LinkedIndividual = {
  assurance?: Person["link"] extends Array<infer T>
    ? T extends { assurance?: infer A }
      ? A
      : string
    : string;
  display: string;
  id: string;
  resourceType: IndividualResourceType;
};

export function getSectionResourceType(
  section: IndividualSection
): IndividualResourceType {
  switch (section) {
    case "patients":
      return "Patient";
    case "practitioners":
      return "Practitioner";
    case "users":
    default:
      return "Person";
  }
}

export function getResourceSection(
  resourceType: IndividualResourceType
): IndividualSection {
  switch (resourceType) {
    case "Patient":
      return "patients";
    case "Practitioner":
      return "practitioners";
    case "Person":
    default:
      return "users";
  }
}

export function normalizeIndividualResourceType(
  value?: string | null
): IndividualResourceType | null {
  const normalizedValue = (value ?? "").trim().toLowerCase();

  if (normalizedValue === "person") {
    return "Person";
  }

  if (normalizedValue === "patient") {
    return "Patient";
  }

  if (normalizedValue === "practitioner") {
    return "Practitioner";
  }

  return null;
}

export function getFullName(resource?: IndividualResource | null): string {
  const names = resource?.name ?? [];

  for (const name of names) {
    const family = name.family?.trim() ?? "";
    const given = (name.given ?? []).map((entry) => entry.trim()).filter(Boolean);
    const combined = [given.join(" "), family].filter(Boolean).join(" ").trim();

    if (combined) {
      return combined;
    }
  }

  return resource?.id ?? "N/A";
}

export function getFamilyName(resource?: IndividualResource | null): string {
  return resource?.name?.[0]?.family?.trim() ?? "N/A";
}

export function getGivenNames(resource?: IndividualResource | null): string {
  const values =
    resource?.name?.[0]?.given?.map((entry) => entry.trim()).filter(Boolean) ?? [];

  return values.length > 0 ? values.join(" ") : "N/A";
}

export function getPrimaryIdentifier(resource?: IndividualResource | null): string {
  return (
    getOfficialIdentifierValue(resource?.identifier) ||
    resource?.identifier?.[0]?.value?.trim() ||
    resource?.id ||
    "N/A"
  );
}

export function getIdentifiers(resource?: IndividualResource | null) {
  return (
    resource?.identifier
      ?.map((identifier, index) => ({
        key:
          [
            identifier.system ?? "",
            identifier.value ?? "",
            identifier.use ?? "",
            String(index),
          ].join("-") || `identifier-${index}`,
        system: identifier.system ?? "N/A",
        value: identifier.value ?? "N/A",
        use: identifier.use ?? "N/A",
      }))
      .filter((identifier) => identifier.system !== "N/A" || identifier.value !== "N/A") ??
    []
  );
}

export function getTelecomEntries(resource?: IndividualResource | null) {
  return (
    resource?.telecom
      ?.map((telecom, index) => ({
        key:
          [telecom.system ?? "", telecom.value ?? "", telecom.use ?? "", String(index)].join(
            "-"
          ) || `telecom-${index}`,
        system: telecom.system ?? "N/A",
        value: telecom.value ?? "N/A",
        use: telecom.use ?? "N/A",
      }))
      .filter((telecom) => telecom.value !== "N/A") ?? []
  );
}

export function getTelecomValue(
  resource: IndividualResource | null | undefined,
  system: string
): string {
  return (
    resource?.telecom?.find((telecom) => telecom.system === system)?.value ?? "N/A"
  );
}

export function getAddressEntries(resource?: Patient | null) {
  return (
    resource?.address
      ?.map((address, index) => {
        const lines = [
          ...(address.line ?? []).map((entry) => entry.trim()).filter(Boolean),
          address.postalCode?.trim() ?? "",
          address.city?.trim() ?? "",
          address.country?.trim() ?? "",
        ].filter(Boolean);

        return {
          key: `${index}-${lines.join("-")}` || `address-${index}`,
          use: address.use ?? "N/A",
          value: lines.join(", ") || "N/A",
        };
      })
      .filter((address) => address.value !== "N/A") ?? []
  );
}

export function getQualificationEntries(resource?: Practitioner | null) {
  return (
    resource?.qualification
      ?.map((qualification, index) => {
        const label =
          qualification.code?.text?.trim() ||
          qualification.code?.coding
            ?.map((coding) => coding.display?.trim() || coding.code?.trim() || "")
            .filter(Boolean)
            .join(", ") ||
          "N/A";

        const issuer =
          qualification.issuer?.display?.trim() ||
          getReferenceValue(qualification.issuer) ||
          "N/A";

        return {
          key: `${index}-${label}-${issuer}`,
          label,
          issuer,
        };
      })
      .filter((qualification) => qualification.label !== "N/A") ?? []
  );
}

export function formatBirthDate(value?: string | null): string {
  if (!value) {
    return "N/A";
  }

  const parsedDate = dayjs(value);

  if (!parsedDate.isValid()) {
    return value;
  }

  return parsedDate.format("DD/MM/YYYY");
}

export function formatAge(value?: string | null): string {
  if (!value) {
    return "N/A";
  }

  const parsedDate = dayjs(value);

  if (!parsedDate.isValid()) {
    return "N/A";
  }

  return String(dayjs().diff(parsedDate, "year"));
}

export function formatStatusValue(
  resource?: IndividualResource | null
): string {
  if (resource?.active === true) {
    return "active";
  }

  if (resource?.active === false) {
    return "inactive";
  }

  return "unknown";
}

export function findResourceByIdOrIdentifier<T extends IndividualResource>(
  resources: T[],
  searchValue?: string | null
): T | null {
  const normalizedSearchValue = (searchValue ?? "").trim().toLowerCase();

  if (!normalizedSearchValue) {
    return null;
  }

  return (
    resources.find((resource) => {
      const candidates = [
        resource.id ?? "",
        getPrimaryIdentifier(resource),
        ...((resource.identifier ?? []).map((identifier) => identifier.value ?? "")),
        getFullName(resource),
      ]
        .map((candidate) => candidate.trim().toLowerCase())
        .filter(Boolean);

      return candidates.includes(normalizedSearchValue);
    }) ?? null
  );
}

export function resolveLinkedIndividuals(
  person: Person,
  patients: Patient[],
  practitioners: Practitioner[]
): LinkedIndividual[] {
  const linkedIndividuals =
    person.link?.map((link, index) => {
        const reference = link.target;
        const referenceValue = getReferenceValue(reference);
        const referenceType = normalizeIndividualResourceType(
          reference?.reference?.split("/")?.[0]
        );

        if (referenceType === "Patient") {
          const patient =
            patients.find((candidate) => candidate.id === referenceValue) ??
            findByReference(patients, reference);

          return {
            assurance: link.assurance,
            display:
              patient ? getFullName(patient) : reference?.display ?? referenceValue ?? "N/A",
            id: patient?.id ?? referenceValue,
            resourceType: "Patient" as const,
          };
        }

        if (referenceType === "Practitioner") {
          const practitioner =
            practitioners.find((candidate) => candidate.id === referenceValue) ??
            findByReference(practitioners, reference);

          return {
            assurance: link.assurance,
            display:
              practitioner
                ? getFullName(practitioner)
                : reference?.display ?? referenceValue ?? "N/A",
            id: practitioner?.id ?? referenceValue,
            resourceType: "Practitioner" as const,
          };
        }

        return {
          assurance: link.assurance,
          display: reference?.display ?? referenceValue ?? `Lien ${index + 1}`,
          id: referenceValue,
          resourceType: "Person" as const,
        };
      }) ?? [];

  return linkedIndividuals.filter(
    (linked) => Boolean(linked.id && linked.display)
  ) as LinkedIndividual[];
}

export function findLinkedPersonsForTarget(
  people: Person[],
  resourceType: "Patient" | "Practitioner",
  resourceId?: string | null
): Person[] {
  const normalizedResourceId = (resourceId ?? "").trim();

  if (!normalizedResourceId) {
    return [];
  }

  return people.filter((person) =>
    person.link?.some((link) => {
      const targetReference = link.target?.reference?.trim() ?? "";
      return (
        targetReference === `${resourceType}/${normalizedResourceId}` ||
        getReferenceValue(link.target) === normalizedResourceId
      );
    })
  );
}

export function getReferenceLabel(reference?: Reference | null): string {
  return getReferenceValue(reference) || reference?.display?.trim() || "N/A";
}

function findByReference<T extends IndividualResource>(
  resources: T[],
  reference?: Reference | null
): T | null {
  const referenceValue = getReferenceValue(reference);

  if (!referenceValue) {
    return null;
  }

  return (
    resources.find((candidate) => {
      const identifiers = candidate.identifier?.map((identifier) => identifier.value ?? "") ?? [];
      return [candidate.id ?? "", ...identifiers].includes(referenceValue);
    }) ?? null
  );
}
